import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../chat/api";

/* Chat history — the permanent left rail on /rfp-copilot.
 *
 * Redesign notes (what changed from the previous version and why):
 *
 *  1. "New chat" is an outlined accent BUTTON, not a list row. As a row it sat
 *     in the same visual class as the chat titles under it, so the one action
 *     that starts work looked like the eleventh thing you could click.
 *  2. Active row is accent-tinted with a 2.5px accent bar. Active and hover
 *     were both neutral greys before — at a glance you could not tell which
 *     conversation you were in.
 *  3. Pinned group. Bid teams return to the same two or three chats all week;
 *     date buckets push them down the list every day.
 *  4. Date headers are sticky, so scrolling never loses the bucket you are in.
 *  5. Search is a filled field — a hairline border on a near-white ground read
 *     as decoration rather than as something you can type in. ⌘K focuses it,
 *     unadvertised; the shortcut badges that were here have been removed.
 *  6. Account foot. The column previously ran off the bottom of the screen
 *     with nothing to land on, and the account lived two rails away.
 *  7. Header is one line, and the wordmark alone carries it — no glyph beside
 *     it. Two marks saying the same thing competed and neither had room to be
 *     the identity. The tagline went for the same reason: chrome read once and
 *     then ignored, costing ~40px of list.
 *
 * Palette is unchanged: #FCFBFC ground, #18101C ink, #A04A97 accent,
 * #77707E muted.
 */

const CHAT_MODULE = "workspace_copilot";
const PIN_KEY = "rfp_copilot_pinned";

const relTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Date buckets rather than a flat list: "which chat was that" is nearly always
// remembered as "the one from yesterday", not by title.
const bucketise = (rows) => {
  const labels = ["Today", "Yesterday", "Previous 7 days", "Previous 30 days", "Older"];
  const buckets = labels.map((label) => ({ label, items: [] }));
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const DAY = 86400000;

  rows.forEach((s) => {
    const t = new Date(s.last_message_timestamp).getTime();
    let idx = 4;
    if (!Number.isNaN(t)) {
      if (t >= startOfToday.getTime()) idx = 0;
      else {
        const daysAgo = Math.floor((startOfToday.getTime() - t) / DAY);
        idx = daysAgo < 1 ? 1 : daysAgo < 7 ? 2 : daysAgo < 30 ? 3 : 4;
      }
    }
    buckets[idx].items.push(s);
  });

  return buckets.filter((b) => b.items.length);
};

/* Pins are a per-user client preference — there is no server field for them, so
 * they live in localStorage. If a pins endpoint lands later, swap readPins /
 * writePins for the API calls and nothing else in this file changes. */
const readPins = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};
const writePins = (ids) => {
  try {
    localStorage.setItem(PIN_KEY, JSON.stringify(ids));
  } catch {
    /* private mode — pins just won't persist */
  }
};

/* ---------------------------------------------------------------- icons -- */

const PinIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <path d="M12 17v5" />
    <path d="M9 10.76V4h6v6.76a2 2 0 0 0 .55 1.38L18 15H6l2.45-2.86A2 2 0 0 0 9 10.76Z" />
  </svg>
);

const PencilIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

/* ------------------------------------------------------------------ row -- */

const ChatRow = ({
  session, active, pinned, confirming, busy, opening,
  onOpen, onTogglePin, onRename, onAskDelete, onConfirmDelete, onCancelDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(session.title || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = value.trim();
    setEditing(false);
    if (next && next !== session.title) onRename?.(session.chat_id, next);
    else setValue(session.title || "");
  };

  if (editing) {
    return (
      <div className="rounded-[9px] bg-white ring-2 ring-[#A04A97]/45">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setValue(session.title || "");
              setEditing(false);
            }
          }}
          aria-label="Chat title"
          className="w-full rounded-[9px] bg-transparent px-3 py-[9px] text-[13.5px] text-[#18101C] outline-none"
        />
      </div>
    );
  }

  return (
    <div
      className={`group/row relative rounded-[9px] transition-colors duration-200 ${
        active ? "bg-[#A04A97]/10" : "hover:bg-[#18101C]/[0.04]"
      }`}
    >
      {/* Active is accent, not another grey — hover and active were previously
          indistinguishable at a glance. */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2 bottom-2 w-[2.5px] rounded-r-[3px] bg-[#A04A97]"
        />
      )}

      <button
        type="button"
        onClick={() => onOpen(session.chat_id)}
        disabled={confirming || busy}
        title={session.title || "Untitled chat"}
        className="flex w-full items-center gap-2 rounded-[9px] py-[9px] pl-3 pr-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] disabled:cursor-default"
      >
        <span
          className={`min-w-0 flex-1 truncate text-[13.5px] leading-snug ${
            active ? "font-medium text-[#18101C]" : "text-[#3F3F46]"
          }`}
        >
          {session.title || "Untitled chat"}
        </span>
        {/* #77707E is the lightest grey on the RAIL that still clears AA at
            11px — 4.62:1. Note it does not survive being moved onto the search
            field, which is darker; the greys in there are a step down for that
            reason, not by accident. Text recedes here by size, never by fading
            below legibility. */}
        <span className="flex-none text-[11px] tabular-nums text-[#77707E] transition-opacity duration-200 group-hover/row:opacity-0">
          {opening ? "…" : relTime(session.last_message_timestamp)}
        </span>
      </button>

      {confirming ? (
        /* A small confirmation anchored under the row's right edge, rather than
           a strip laid across the row.
           The old version covered the chat title with a bright red pill — it
           hid the very thing you were being asked about, and shouted louder
           than deleting one conversation deserves. This leaves the row intact
           and readable while you decide. */
        <>
          {/* Keeps the row looking selected while its menu is open. */}
          <span aria-hidden="true" className="absolute inset-0 rounded-[9px] bg-[#18101C]/[0.04]" />
          <div
            role="dialog"
            aria-label="Confirm delete"
            className="absolute right-1 top-full z-30 mt-1 w-[186px] overflow-hidden rounded-xl border border-[#18101C]/[0.10] bg-white shadow-[0_2px_6px_rgba(24,16,28,0.06),0_14px_28px_-14px_rgba(24,16,28,0.30)] animate-copilot-rise motion-reduce:animate-none"
          >
            <p className="px-3 pb-1.5 pt-2.5 text-[12.5px] leading-snug text-[#3F3F46]">
              Delete this chat?
            </p>
            <div className="flex items-center justify-end gap-1 px-2 pb-2">
              <button
                type="button"
                onClick={onCancelDelete}
                className="rounded-lg px-2 py-1 text-[12px] font-medium text-[#6B6472] transition-colors duration-200 hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
              >
                Cancel
              </button>
              {/* Cancel first, delete last: the destructive one is never the
                  button your hand is already over when the menu appears. */}
              <button
                type="button"
                onClick={() => onConfirmDelete(session.chat_id)}
                disabled={busy}
                aria-label={`Delete chat: ${session.title || "Untitled chat"}`}
                className="rounded-lg bg-[#B91C1C] px-2.5 py-1 text-[12px] font-semibold text-white transition-colors duration-200 hover:bg-[#991B1B] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B91C1C] focus-visible:ring-offset-2 motion-reduce:transition-none"
              >
                {busy ? "…" : "Delete"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 gap-0.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover/row:opacity-100">
          <button
            type="button"
            onClick={() => onTogglePin(session.chat_id)}
            aria-label={pinned ? "Unpin chat" : "Pin chat"}
            title={pinned ? "Unpin" : "Pin"}
            className={`flex h-6 w-6 items-center justify-center rounded-[7px] transition-colors duration-200 hover:bg-[#18101C]/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] ${
              pinned ? "text-[#A04A97]" : "text-[#77707E] hover:text-[#18101C]"
            }`}
          >
            <PinIcon width="13" height="13" />
          </button>
          {onRename && (
            <button
              type="button"
              onClick={() => {
                setValue(session.title || "");
                setEditing(true);
              }}
              aria-label={`Rename chat: ${session.title || "Untitled chat"}`}
              title="Rename"
              className="flex h-6 w-6 items-center justify-center rounded-[7px] text-[#77707E] transition-colors duration-200 hover:bg-[#18101C]/[0.07] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              <PencilIcon width="13" height="13" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onAskDelete(session.chat_id)}
            aria-label={`Delete chat: ${session.title || "Untitled chat"}`}
            title="Delete"
            className="flex h-6 w-6 items-center justify-center rounded-[7px] text-[#77707E] transition-colors duration-200 hover:bg-[#DC2626]/10 hover:text-[#DC2626] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DC2626]"
          >
            <TrashIcon width="13" height="13" />
          </button>
        </div>
      )}
    </div>
  );
};

/* ----------------------------------------------------------------- rail -- */

const CopilotHistory = ({
  activeChatId,
  onSelect,
  onNewChat,
  onCollapse,
  refreshKey,
  /* Optional. Render the rename affordance only when the caller can persist it
     — there is no rename endpoint today, so passing this is a deliberate act.
     Signature: (chatId, title) => Promise|void */
  onRenameSession,
}) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [pins, setPins] = useState(readPins);

  const searchRef = useRef(null);
  const loadedOnceRef = useRef(false);
  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get("/api/v1/rfp/kb-copilot/chats/", {
        params: { chat_module: CHAT_MODULE },
      });
      setSessions(res?.data?.chat_sessions || []);
      setError(null);
    } catch (err) {
      // 404 means "this user has no sessions yet" — an empty state, not a
      // failure. Showing an error there makes a first-time user think the
      // product is broken.
      if (err?.response?.status === 404) {
        setSessions([]);
        setError(null);
      } else {
        setError("Couldn't load your chats.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Loads on mount, then revalidates silently whenever a turn completes — so the
  // list never flashes back to skeletons while you are using the page.
  useEffect(() => {
    load({ silent: loadedOnceRef.current });
    loadedOnceRef.current = true;
    setPendingDelete(null);
  }, [refreshKey, load]);

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPendingDelete(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingDelete]);

  /* ⌘K focuses search.
   *
   * ⌘N used to start a chat and has been removed with its badge: browsers
   * reserve Cmd/Ctrl+N for a new window and ignore preventDefault, so it could
   * never fire. Keeping the handler would have left a keystroke intercepted for
   * nothing. */
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== "k") return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? sessions.filter((s) =>
          `${s.title || ""} ${s.last_message || ""}`.toLowerCase().includes(q)
        )
      : sessions;
  }, [sessions, query]);

  const pinnedRows = useMemo(
    () => filtered.filter((s) => pins.includes(s.chat_id)),
    [filtered, pins]
  );
  const groups = useMemo(
    () => bucketise(filtered.filter((s) => !pins.includes(s.chat_id))),
    [filtered, pins]
  );

  const handleOpen = async (chatId) => {
    if (chatId === activeChatId || busyId || openingId) return;
    setOpeningId(chatId);
    try {
      // Scoped to Sensa's own turns — a chat_id can be shared with another
      // chat surface, and without this the rail opens both interleaved.
      const res = await api.get(
        `/api/v1/copilot/get-chat-sessions/${chatId}/?chat_module=workspace_copilot`
      );
      onSelect?.(chatId, res?.data?.chat_history || []);
    } catch {
      setError("Couldn't open that chat.");
    } finally {
      setOpeningId(null);
    }
  };

  const handleDelete = async (chatId) => {
    setBusyId(chatId);
    try {
      await api.post("/api/v1/copilot/delete-session/", { chat_id: chatId });
      setSessions((prev) => prev.filter((s) => s.chat_id !== chatId));
      setPins((prev) => {
        const next = prev.filter((id) => id !== chatId);
        writePins(next);
        return next;
      });
    } catch {
      setError("Couldn't delete that chat.");
    } finally {
      setBusyId(null);
      setPendingDelete(null);
    }
  };

  const togglePin = (chatId) =>
    setPins((prev) => {
      const next = prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [chatId, ...prev];
      writePins(next);
      return next;
    });

  const handleRename = onRenameSession
    ? async (chatId, title) => {
        // Optimistic: the row is already showing the new title.
        setSessions((prev) =>
          prev.map((s) => (s.chat_id === chatId ? { ...s, title } : s))
        );
        try {
          await onRenameSession(chatId, title);
        } catch {
          setError("Couldn't rename that chat.");
          load({ silent: true });
        }
      }
    : undefined;

  const rowProps = (s) => ({
    session: s,
    active: s.chat_id === activeChatId,
    pinned: pins.includes(s.chat_id),
    confirming: pendingDelete === s.chat_id,
    busy: busyId === s.chat_id,
    opening: openingId === s.chat_id,
    onOpen: handleOpen,
    onTogglePin: togglePin,
    onRename: handleRename,
    onAskDelete: setPendingDelete,
    onConfirmDelete: handleDelete,
    onCancelDelete: () => setPendingDelete(null),
  });

  const empty = !loading && !pinnedRows.length && !groups.length;

  return (
    <aside className="flex h-full w-[276px] flex-none flex-col border-r border-[#18101C]/[0.07] bg-[#FCFBFC]">
      {/* Identity on one line: mark + wordmark. The tagline under it was chrome
          read once and then ignored, and it cost ~40px of list. */}
      {/* items-start, not items-center: the collapse button lines up with the
          wordmark rather than floating to the middle of a two-line block. */}
      <div className="flex flex-none items-start pl-3.5 pr-3 pt-4">
        <div className="min-w-0 flex-1">
        {/* The wordmark IS the logo now — the sparkle mark beside it is gone.
            Two marks competed: a glyph and a word, both saying the same thing,
            neither given room to be the identity.

            Size does the work the mark used to. At 26px it also crosses WCAG's
            large-text threshold (24px), where the bar drops from 4.5:1 to 3:1 —
            which is what finally allows the gradient to end on the magenta the
            design asks for. #B667B2 measures 3.65:1: too light for body copy,
            correct and compliant for a mark this size. It is the one place in
            the product where that trade is available.

            No shadow: on a gradient this dark at one end, a drop-shadow reads
            as smudge rather than lift — there is nothing for it to separate the
            mark FROM. */}
        <h1 className="bg-gradient-to-br from-[#2B1A28] via-[#7A4574] to-[#B667B2] bg-clip-text text-[26px] font-semibold leading-none tracking-[-0.03em] text-transparent forced-colors:bg-none forced-colors:text-[#18101C]">
          Sensa
        </h1>
        {/* The line the redesign cut as "chrome read once and then ignored".
            Kept: on a rail whose only other words are chat titles, it is the
            one line that says what the product is, and it costs two. */}
        <p className="mt-1.5 text-[12px] leading-snug text-[#77707E]">
          Bid intelligence for every RFP
        </p>
        </div>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Hide chats"
            title="Hide chats"
            className="-mr-1 mt-0.5 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] text-[#77707E] transition-colors duration-200 hover:bg-[#18101C]/[0.05] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          >
            <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
              <path d="M17 10l-2 2 2 2" />
            </svg>
          </button>
        )}
      </div>

      {/* The rail's primary action, and now shaped like one: an accent outline,
          per the outlined-primary convention this product uses on its buttons.
          As a bare row it read as the eleventh item in the list below it. */}
      <div className="flex-none px-3 pt-6">
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center gap-2 rounded-[10px] border border-[#A04A97]/45 bg-[#A04A97]/[0.06] px-[11px] py-[9px] text-left text-[14px] font-medium text-[#8B3F83] transition-colors duration-200 hover:border-[#A04A97]/70 hover:bg-[#A04A97]/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
        >
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New chat
        </button>
      </div>

      {/* A filled field, not a hairline box: on a #FCFBFC ground a 7%-alpha
          border read as decoration rather than as something you can type in.
          Icon and shortcut are positioned against the INPUT, not the padded
          container, so the offsets mean what they say. */}
      <div className="flex-none px-3 pb-3 pt-2.5">
        <div className="relative">
          <svg aria-hidden="true"
            className="pointer-events-none absolute left-[9px] top-1/2 -translate-y-1/2 text-[#77707E]"
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search chats"
            placeholder="Search chats"
            className="w-full rounded-[9px] border border-transparent bg-[#18101C]/[0.045] py-2 pl-[30px] pr-8 text-[13.5px] text-[#18101C] outline-none transition-[background-color,border-color,box-shadow] duration-200 placeholder:text-[#6E6775] focus:border-[#A04A97]/55 focus:bg-white focus:shadow-[0_0_0_3px_rgba(160,74,151,0.10)]"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[#77707E] hover:text-[#18101C]"
            >
              <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      <div className="sensa-scroll sensa-scroll-wide min-h-0 flex-1 overflow-y-auto border-t border-[#18101C]/[0.06] px-2 pb-3">
        {error && (
          <p className="mx-1 mb-2 mt-2 rounded-lg bg-[#FEF2F2] px-2.5 py-2 text-[12.5px] text-[#B91C1C]">
            {error}
          </p>
        )}

        {loading &&
          // Skeletons shaped like the rows they replace, so the list doesn't
          // jump when the data lands.
          [0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-lg px-3 py-[9px]">
              <div className="h-[10px] animate-pulse rounded bg-[#18101C]/[0.07]"
                   style={{ width: `${52 + ((i * 13) % 34)}%` }} />
            </div>
          ))}

        {empty && (
          <div className="px-3 py-9 text-center">
            <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-[#A04A97]/[0.08] text-[#A04A97]">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>
            <p className="mt-2.5 text-[13.5px] font-medium text-[#18101C]">
              {query ? "No matches" : "No chats yet"}
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-[#77707E]">
              {query
                ? "Try a different word from the question you asked."
                : "Your conversations will show up here."}
            </p>
          </div>
        )}

        {!!pinnedRows.length && (
          <>
            {/* Sticky headers: on a list this long the bucket you are reading
                scrolls out of sight long before the rows in it do. */}
            <div className="sticky top-0 z-[2] flex items-center gap-1.5 bg-[#FCFBFC] px-1.5 pb-1.5 pt-3.5">
              <PinIcon width="11" height="11" className="text-[#A04A97]" />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-[#A04A97]">
                Pinned
              </span>
            </div>
            {pinnedRows.map((s) => (
              <ChatRow key={s.chat_id} {...rowProps(s)} />
            ))}
          </>
        )}

        {groups.map((group) => (
          <div key={group.label}>
            <div className="sticky top-0 z-[2] bg-[#FCFBFC] px-1.5 pb-1.5 pt-4">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-[#746E7A]">
                {group.label}
              </span>
            </div>
            {group.items.map((s) => (
              <ChatRow key={s.chat_id} {...rowProps(s)} />
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
};

export default CopilotHistory;
